import { useEffect, useState } from 'react'
import { Alert, Button, ListGroup, Modal, Spinner } from 'react-bootstrap'
import { t } from '../locale'
import {
  getSharePointFolderItems,
  getSharePointRootItems,
  getSharePointSite,
  SharePointDriveItem,
} from '../service/sharepoint'

type FolderHistoryItem = {
  id?: string
  name: string
}

type Props = {
  show: boolean
  onHide: () => void
  onSelect: (item: SharePointDriveItem) => void
}

export default function SharePointFilePickerModal({ show, onHide, onSelect }: Props) {
  const [items, setItems] = useState<SharePointDriveItem[]>([])
  const [history, setHistory] = useState<FolderHistoryItem[]>([{ name: 'Documents' }])
  const [loading, setLoading] = useState(false)
  const [siteConfigured, setSiteConfigured] = useState<boolean | null>(null)

  useEffect(() => {
    if (!show) return

    setItems([])
    setHistory([{ name: 'Documents' }])
    setSiteConfigured(null)
    loadRoot()
  }, [show])

  async function loadRoot() {
    setLoading(true)

    try {
      const siteResult = await getSharePointSite()

      if (!siteResult.site) {
        setSiteConfigured(false)
        setItems([])
        return
      }

      setSiteConfigured(true)

      const result = await getSharePointRootItems()

      setItems(Array.isArray(result.items) ? result.items : [])
    } finally {
      setLoading(false)
    }
  }

  async function openFolder(item: SharePointDriveItem) {
    if (!item.folder) return

    setLoading(true)

    try {
      const result = await getSharePointFolderItems(item.id)

      setItems(Array.isArray(result.items) ? result.items : [])
      setHistory(current => [...current, { id: item.id, name: item.name }])
    } finally {
      setLoading(false)
    }
  }

  async function goBack() {
    if (history.length <= 1) return

    const newHistory = history.slice(0, -1)
    const parent = newHistory[newHistory.length - 1]

    setLoading(true)

    try {
      const result = parent.id
        ? await getSharePointFolderItems(parent.id)
        : await getSharePointRootItems()

      setItems(Array.isArray(result.items) ? result.items : [])
      setHistory(newHistory)
    } finally {
      setLoading(false)
    }
  }

  function handleItemClick(item: SharePointDriveItem) {
    if (item.folder) {
      openFolder(item)
      return
    }

    onSelect(item)
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('selectSharePointFile')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {siteConfigured === false && (
          <Alert variant='warning'>{t('sharePointSiteNotConfigured')}</Alert>
        )}

        {siteConfigured !== false && (
          <>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div className='text-muted'>{history.map(item => item.name).join(' / ')}</div>

              <Button
                type='button'
                variant='outline-secondary'
                size='sm'
                disabled={history.length <= 1 || loading}
                onClick={goBack}
              >
                <i className='bi bi-arrow-left me-1' />
                {t('back')}
              </Button>
            </div>

            {loading ? (
              <div className='text-center py-5'>
                <Spinner animation='border' />
              </div>
            ) : (
              <ListGroup>
                {items.length === 0 && (
                  <div className='text-muted text-center py-4'>{t('sharePointFolderEmpty')}</div>
                )}

                {items.map(item => (
                  <ListGroup.Item
                    key={item.id}
                    action
                    onClick={() => handleItemClick(item)}
                    className='d-flex align-items-center justify-content-between'
                  >
                    <div className='d-flex align-items-center'>
                      <i
                        className={`bi ${
                          item.folder
                            ? 'bi-folder-fill text-warning'
                            : 'bi-file-earmark text-primary'
                        } me-3 fs-5`}
                      />

                      <div>
                        <div className='fw-medium'>{item.name}</div>

                        {!item.folder && item.size !== undefined && (
                          <div className='small text-muted'>{(item.size / 1024).toFixed(1)} KB</div>
                        )}
                      </div>
                    </div>

                    {item.folder && <i className='bi bi-chevron-right text-muted' />}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button type='button' variant='secondary' onClick={onHide}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
